import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { api, friendlyError } from '../lib/api-client';
import { formatDate, formatDateTime } from '../i18n/formatters';
import {
  PartnerDashboardResponse,
  commissionStatusBadgeVariant,
  commissionStatusLabel,
  formatPartnerMoney,
  payoutStatusBadgeVariant,
  payoutStatusLabel,
  referralSourceLabel,
  referralStatusBadgeVariant,
  referralStatusLabel,
} from '../lib/partner-portal';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import StatCard from '../components/ui/StatCard';
import Badge from '../components/ui/Badge';
import AlertBanner from '../components/ui/AlertBanner';
import EmptyState from '../components/ui/EmptyState';
import {
  PartnerHeaderMeta,
  PartnerReferralLinkCard,
  portalTableCellStyle,
  portalTableHeaderStyle,
} from '../components/partner-portal/shared';

const C = {
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
};

function formatMetricAmount(value: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function PartnerDashboardPage() {
  const { t } = useTranslation(['partnerPortal', 'common']);
  const { partnerAccess } = useAuth();
  const [dashboard, setDashboard] = useState<PartnerDashboardResponse['dashboard'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await api.get<PartnerDashboardResponse>('/partner/dashboard');
        setDashboard(data.dashboard);
      } catch (err) {
        setError(friendlyError(err, t('errors.load')));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  const metrics = dashboard?.metrics;
  const nextStep = metrics
    ? metrics.totalReferrals === 0
      ? {
          eyebrow: t('dashboard.nextStep.firstReferralEyebrow'),
          title: t('dashboard.nextStep.firstReferralTitle'),
          body: t('dashboard.nextStep.firstReferralBody'),
        }
      : metrics.customerReferrals === 0
        ? {
            eyebrow: t('dashboard.nextStep.pipelineEyebrow'),
            title: t('dashboard.nextStep.pipelineTitle'),
            body: t('dashboard.nextStep.pipelineBody'),
          }
        : metrics.pendingCommission > 0
          ? {
              eyebrow: t('dashboard.nextStep.payoutEyebrow'),
              title: t('dashboard.nextStep.payoutTitle'),
              body: t('dashboard.nextStep.payoutBody'),
            }
          : {
              eyebrow: t('dashboard.nextStep.growthEyebrow'),
              title: t('dashboard.nextStep.growthTitle'),
              body: t('dashboard.nextStep.growthBody'),
            }
    : null;

  return (
    <Container maxWidth={1320}>
      <div style={{ display: 'grid', gap: 20 }}>
        <PageHeader
          eyebrow={t('dashboard.eyebrow')}
          title={t('dashboard.title')}
          subtitle={dashboard?.partner.name ?? partnerAccess?.partnerName ?? t('common:navigation.partnerPortal')}
          description={t('dashboard.description')}
          meta={dashboard ? <PartnerHeaderMeta access={dashboard.access} /> : undefined}
        />

        {copyFeedback && (
          <AlertBanner variant="success">
            {copyFeedback}
          </AlertBanner>
        )}

        {error && (
          <AlertBanner variant="danger">
            {error}
          </AlertBanner>
        )}

        {loading ? (
          <Card>{t('common:actions.loading')}</Card>
        ) : !dashboard || !metrics ? (
          <EmptyState
            title={t('empty.referralsTitle')}
            description={t('empty.referralsDescription')}
          />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 18 }}>
              <StatCard
                label={t('cards.totalRevenueGenerated')}
                value={formatMetricAmount(metrics.totalRevenueGenerated)}
                hint={t('cardsHints.totalRevenueGenerated')}
              />
              <StatCard
                label={t('cards.totalCommissionEarned')}
                value={formatMetricAmount(metrics.totalCommissionEarned)}
                hint={t('cardsHints.totalCommissionEarned')}
              />
              <StatCard
                label={t('cards.pendingCommission')}
                value={formatMetricAmount(metrics.pendingCommission)}
                accent="var(--acc-color-warning)"
                hint={t('cardsHints.pendingCommission')}
              />
              <StatCard
                label={t('cards.approvedCommission')}
                value={formatMetricAmount(metrics.approvedCommission)}
                accent="var(--acc-color-primary)"
                hint={t('cardsHints.approvedCommission')}
              />
              <StatCard
                label={t('cards.paidCommission')}
                value={formatMetricAmount(metrics.paidCommission)}
                accent="var(--acc-color-success)"
                hint={t('cardsHints.paidCommission')}
              />
              <StatCard
                label={t('cards.totalReferrals')}
                value={metrics.totalReferrals}
                hint={t('cardsHints.totalReferrals')}
              />
              <StatCard
                label={t('cards.customerReferrals')}
                value={metrics.customerReferrals}
                accent="var(--acc-color-success)"
                hint={t('cardsHints.customerReferrals')}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 1fr)', gap: 20 }}>
              <PartnerReferralLinkCard
                label={t('fields.referralLink')}
                title={t('dashboard.referralLinkTitle')}
                description={t('dashboard.referralLinkDescription')}
                note={t('dashboard.referralLinkNote')}
                referralLink={dashboard.referralLink}
                copyLabel={t('actions.copyLink')}
                tone="hero"
                onCopied={() => setCopyFeedback(t('actions.copied'))}
                onCopyFailed={() => setCopyFeedback(t('actions.copyFailed'))}
              />

              <Card>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {nextStep?.eyebrow ?? t('dashboard.nextStep.eyebrow')}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', color: C.text }}>
                    {nextStep?.title ?? t('dashboard.nextStep.title')}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.7, color: C.muted }}>
                    {nextStep?.body ?? t('dashboard.nextStep.body')}
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginTop: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: C.muted, fontSize: 13 }}>{t('dashboard.nextStep.referralsLabel')}</span>
                      <strong style={{ color: C.text }}>{metrics.totalReferrals}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: C.muted, fontSize: 13 }}>{t('dashboard.nextStep.customersLabel')}</span>
                      <strong style={{ color: C.text }}>{metrics.customerReferrals}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: C.muted, fontSize: 13 }}>{t('dashboard.nextStep.readyForPayoutLabel')}</span>
                      <strong style={{ color: C.text }}>{formatMetricAmount(metrics.approvedCommission)}</strong>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(320px, 1fr)', gap: 20 }}>
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 12 }}>
                  {t('dashboard.recentReferrals')}
                </div>
                {dashboard.recentReferrals.length === 0 ? (
                  <EmptyState
                    dashed
                    title={t('empty.referralsTitle')}
                    description={t('empty.referralsDescription')}
                  />
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={portalTableHeaderStyle}>{t('fields.company')}</th>
                        <th style={portalTableHeaderStyle}>{t('fields.source')}</th>
                        <th style={portalTableHeaderStyle}>{t('fields.status')}</th>
                        <th style={portalTableHeaderStyle}>{t('fields.attributed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recentReferrals.map((referral) => (
                        <tr key={referral.id}>
                          <td style={portalTableCellStyle}>
                            <div style={{ fontWeight: 700, color: C.text }}>
                              {referral.prospectCompany ?? referral.organisationName ?? referral.prospectName ?? t('common:labels.notAvailable')}
                            </div>
                            <div style={{ color: C.muted, marginTop: 4 }}>
                              {referral.organisationName ?? referral.prospectEmail ?? t('common:labels.notAvailable')}
                            </div>
                          </td>
                          <td style={portalTableCellStyle}>{referralSourceLabel(referral.sourceType, t)}</td>
                          <td style={portalTableCellStyle}>
                            <Badge variant={referralStatusBadgeVariant(referral.status)}>
                              {referralStatusLabel(referral.status, t)}
                            </Badge>
                          </td>
                          <td style={portalTableCellStyle}>{formatDate(referral.attributedAt) || t('common:labels.notAvailable')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 12 }}>
                  {t('dashboard.howToEarnTitle')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>
                  {t('dashboard.howToEarnBody')}
                </div>
                <div style={{ display: 'grid', gap: 10, fontSize: 13, color: C.text }}>
                  <div>1. {t('dashboard.howToEarnPoints.one')}</div>
                  <div>2. {t('dashboard.howToEarnPoints.two')}</div>
                  <div>3. {t('dashboard.howToEarnPoints.three')}</div>
                </div>
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 12 }}>
                  {t('dashboard.recentCommissions')}
                </div>
                {dashboard.recentCommissions.length === 0 ? (
                  <EmptyState
                    dashed
                    title={t('empty.commissionsTitle')}
                    description={t('empty.commissionsDescription')}
                  />
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {dashboard.recentCommissions.map((commission) => (
                      <div
                        key={commission.id}
                        style={{
                          border: `1px solid ${C.border}`,
                          borderRadius: 12,
                          padding: '12px 14px',
                          display: 'grid',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, color: C.text }}>
                            {formatPartnerMoney(commission.currency, commission.commissionAmount)}
                          </div>
                          <Badge variant={commissionStatusBadgeVariant(commission.commissionStatus)}>
                            {commissionStatusLabel(commission.commissionStatus, t)}
                          </Badge>
                        </div>
                        <div style={{ color: C.muted, fontSize: 13 }}>
                          {commission.organisationName ?? t('common:labels.notAvailable')}
                        </div>
                        <div style={{ color: C.muted, fontSize: 12 }}>
                          {formatDate(commission.billingPeriodStart)} - {formatDate(commission.billingPeriodEnd)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 12 }}>
                  {t('dashboard.recentPayouts')}
                </div>
                {dashboard.recentPayouts.length === 0 ? (
                  <EmptyState
                    dashed
                    title={t('empty.payoutsTitle')}
                    description={t('empty.payoutsDescription')}
                  />
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {dashboard.recentPayouts.map((payout) => (
                      <div
                        key={payout.id}
                        style={{
                          border: `1px solid ${C.border}`,
                          borderRadius: 12,
                          padding: '12px 14px',
                          display: 'grid',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, color: C.text }}>
                            {formatPartnerMoney(payout.currency, payout.totalAmount)}
                          </div>
                          <Badge variant={payoutStatusBadgeVariant(payout.status)}>
                            {payoutStatusLabel(payout.status, t)}
                          </Badge>
                        </div>
                        <div style={{ color: C.muted, fontSize: 13 }}>
                          {payout.paymentReference ?? t('common:labels.notRecordedYet')}
                        </div>
                        <div style={{ color: C.muted, fontSize: 12 }}>
                          {formatDateTime(payout.paidAt ?? payout.createdAt) || t('common:labels.notAvailable')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
